import clsx from 'clsx'
import { useMemo, useState } from 'react'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { useInfiniteQuery } from 'react-query'

import { Trash } from 'phosphor-react'
import { ReactComponent as AddSvg } from '../../../assets/svgs/add.svg'
import { ReactComponent as CalendarSvg } from '../../../assets/svgs/calendar.svg'
import { api } from '../../../services/api'
import { type AdministrativeUserDomain } from '../../../types/domain/administrative-users-domain'
import { type AssetAnalystDomain } from '../../../types/domain/asset-analyst-domain'
import { type ClientContactDomain } from '../../../types/domain/client-domain'
import { type ExpertDomain } from '../../../types/domain/expert-domain'
import { Button } from '../../Button'
import { FormGroup } from '../../FormElements/FormGroup'
import { Input } from '../../FormElements/Input'
import { InfiniteSelect } from '../../InfiniteSelect'
import { OpenInputEmail } from './OpenInputEmail'

type GeneralSettingsStepProps = {
  clientId: number
  onGoToNextStep: () => void
}

type SelectOption = {
  value: string
  label: string
  triggerValue?: string
}

type SelectOptions = SelectOption[]

export const GeneralSettingsStep = ({
  clientId,
  onGoToNextStep,
}: GeneralSettingsStepProps) => {
  const [searchedExpert, setSearchedExpert] = useState('')
  const [searchedAnalyst, setSearchedAnalyst] = useState('')
  const [searchedContact, setSearchedContact] = useState('')
  const [searchAdmininistrativeUser, setSearchAdministrativeUser] = useState('')

  const { control, formState, setValue, getValues } = useFormContext()
  const {
    fields: expertFields,
    append: appendNewExpertFields,
    remove: removeExpertFields,
  } = useFieldArray({
    control,
    name: 'experts',
  })

  const {
    fields: analystFields,
    append: appendNewAnalystFields,
    remove: removeAnalystFields,
  } = useFieldArray({
    control,
    name: 'analysts',
  })

  const {
    fields: administrativeUsersFields,
    append: appendNewAdministrativeUserField,
    remove: removeAdministrativeUserField,
  } = useFieldArray({
    control,
    name: 'administrativeUsers',
  })

  const {
    fields: contactFields,
    append: appendNewContactFields,
    remove: removeContactField,
  } = useFieldArray({
    control,
    name: 'contacts',
  })

  const handleAddNewExpertFields = () => {
    appendNewExpertFields({
      name: undefined,
      email: undefined,
    })
  }

  const handleRemoveExpertFields = (id: number) => {
    removeExpertFields(id)
  }

  const handleAddNewAnalystFields = () => {
    appendNewAnalystFields({
      id: '',
      name: '',
      email: '',
    })
  }

  const handleAddNewContactFields = () => {
    appendNewContactFields({
      id: '',
      name: '',
      email: '',
    })
  }

  const handleRemoveAnalystFields = (id: number) => {
    removeAnalystFields(id)
  }

  const handleAddNewResearcherFields = () => {
    appendNewAdministrativeUserField({
      id: '',
      name: '',
      email: '',
    })
  }

  const handleRemoveResearcherFields = (id: number) => {
    removeAdministrativeUserField(id)
  }

  const handleRemoveContactField = (id: number) => {
    removeContactField(id)
  }

  const {
    data: experts,
    hasNextPage: hasNextExpertPages,
    fetchNextPage: fetchNextExpertPages,
  } = useInfiniteQuery(
    ['experts-select', searchedExpert],
    async ({ pageParam = 1 }) => {
      const url = `/experts?page=${pageParam}&sort=name&order=ASC&search=${searchedExpert}`
      const response = await api.get(url)
      return response.data
    },
    {
      getNextPageParam ({ meta }) {
        return meta?.hasNextPage ? meta?.page + 1 : undefined
      },
    }
  )

  const {
    data: contacts,
    hasNextPage: hasNextContactPages,
    fetchNextPage: fetchNextContactPages,
  } = useInfiniteQuery(
    ['contacts-infinite-select', searchedExpert],
    async ({ pageParam = 1 }) => {
      const url = `/clients/${clientId}/contacts?page=${pageParam}&sort=name&order=ASC&search=${searchedContact}`
      const response = await api.get(url)
      return response.data
    },
    {
      getNextPageParam ({ meta }) {
        return meta?.hasNextPage ? meta?.page + 1 : undefined
      },
    }
  )

  const {
    data: analysts,
    hasNextPage: hasNextAnalystsPages,
    fetchNextPage: fetchNextAnalystsPages,
  } = useInfiniteQuery(
    ['analysts-infinite-select', searchedExpert],
    async ({ pageParam = 1 }) => {
      const url = `/asset-analysts?page=${pageParam}&search=${searchedAnalyst}&sort=fullName&order=ASC`
      const response = await api.get(url)
      return response.data
    },
    {
      getNextPageParam ({ meta }) {
        return meta?.hasNextPage ? meta?.page + 1 : undefined
      },
    }
  )

  const {
    data: administrativeUsers,
    hasNextPage: hasNextAdministrativeUsers,
    fetchNextPage: fetchNextAdministrativeUsersPage,
  } = useInfiniteQuery(
    ['administrative-users', searchAdmininistrativeUser],
    async ({ pageParam = 1 }) => {
      const url = `/users/administratives?page=${pageParam}&search=${searchAdmininistrativeUser}&sort=name&order=ASC`
      const response = await api.get(url)
      return response.data
    },
    {
      getNextPageParam ({ meta }) {
        return meta?.hasNextPage ? meta?.page + 1 : undefined
      },
    }
  )

  const allAdministrativeUsers: AdministrativeUserDomain[] = useMemo(() => {
    return (
      administrativeUsers?.pages?.reduce(
        (acc: AdministrativeUserDomain[], { data }) => [...acc, ...data],
        []
      ) || []
    )
  }, [administrativeUsers])

  const administrativeUsersOptions: SelectOptions = useMemo(() => {
    const options = allAdministrativeUsers.map(({ name, email }) => ({
      label: name,
      value: email,
    }))

    const [selectedAdministrativeUser] = getValues('administrativeUsers')

    const administrativeUserAlreadySelected =
      selectedAdministrativeUser &&
      options.some(
        (option) => option.value === selectedAdministrativeUser.email
      )

    const selectedAdministrativeUserHasEmptyProperties =
      selectedAdministrativeUser &&
      Object.values(selectedAdministrativeUser).filter(Boolean).length > 0

    if (
      selectedAdministrativeUserHasEmptyProperties &&
      !administrativeUserAlreadySelected
    ) {
      options.push({
        label: selectedAdministrativeUser.name,
        value: String(selectedAdministrativeUser.email),
      })
    }

    return options
  }, [allAdministrativeUsers])

  const expertsOptions: SelectOptions = useMemo(() => {
    const allExperts: ExpertDomain[] =
      experts?.pages?.reduce((acc, { data }) => [...acc, ...data], []) || []

    const options = allExperts.map(({ name, id, email }) => ({
      label: name,
      value: String(id),
      triggerValue: email,
    }))

    const [selectedExpert] = getValues('experts')

    const expertAlreadySelected =
      selectedExpert &&
      options.some((option) => option.value === String(selectedExpert.id))

    const selectedExpertHasEmptyProperties =
      selectedExpert && Object.values(selectedExpert).filter(Boolean).length > 0

    if (selectedExpertHasEmptyProperties && !expertAlreadySelected) {
      options.push({
        label: selectedExpert.name,
        value: String(selectedExpert.id),
        triggerValue: selectedExpert.email,
      })
    }

    return options
  }, [experts])

  const contactsOptions = useMemo(() => {
    const allContacts: ClientContactDomain[] =
      contacts?.pages?.reduce((acc, { data }) => [...acc, ...data], []) || []

    const options = allContacts.map(({ name, id, email }) => ({
      label: name,
      value: String(id),
      triggerValue: email,
    }))

    const selectedContacts = getValues('contacts')

    selectedContacts.forEach(
      (selectedContact: { id: number, name: string, email: string }) => {
        const selectedContactAlreadyLoaded = allContacts.some(
          ({ id }) => String(id) === String(selectedContact.id)
        )

        if (!selectedContactAlreadyLoaded) {
          options.push({
            label: selectedContact.name,
            triggerValue: selectedContact.email,
            value: String(selectedContact.id),
          })
        }
      }
    )

    return options
  }, [contacts?.pages])

  const analystOptions: SelectOptions = useMemo(() => {
    const allAnalysts: AssetAnalystDomain[] =
      analysts?.pages.reduce((acc, { data }) => [...acc, ...data], []) || []

    if (allAnalysts.length === 0) {
      return [{ label: 'Sem analista cadastrado', value: '' }]
    }

    const options = allAnalysts.map(({ id, fullName }) => ({
      label: fullName,
      value: String(id),
    }))

    const selectedAnalysts = getValues('analysts')

    selectedAnalysts.forEach(
      (selectedAnalyst: { id: number, name: string }) => {
        const selectedAnalystAlreadyLoaded = allAnalysts.some(
          ({ id }) => String(id) === String(selectedAnalyst.id)
        )

        if (!selectedAnalystAlreadyLoaded) {
          options.push({
            label: selectedAnalyst.name,
            value: String(selectedAnalyst.id),
          })
        }
      }
    )
    return options
  }, [analysts?.pages])

  const canRemoveContactsField = contactFields.length > 1
  const canRemoveExpertField = expertFields.length > 1
  const canRemoveAnalystField = analystFields.length > 1
  const canRemoveAdministrativeUser = administrativeUsersFields.length > 1

  const getFilteredOptions = (
    fieldName: string,
    currentItemIndex: number,
    options: SelectOptions,
    comparativeProperty = 'id'
  ) => {
    const nonRepeatingValues = getValues(fieldName)?.filter(
      (_: unknown, index: number) => index !== currentItemIndex
    )

    return options?.filter(
      ({ value }) =>
        !nonRepeatingValues?.some(
          ({ [comparativeProperty]: property }: any) =>
            String(property) === String(value)
        )
    )
  }

  return (
    <form className="pr-2 max-h-[80vh] overflow-y-auto scrollbar pb-4">
      <section className="space-y-10">
        <div className="flex flex-col lg:grid lg:grid-flow-col lg:grid-rows-[min-content_auto] lg:grid-cols-2 gap-7">
          <div>
            <legend className="mb-4 text-sm font-bold md:text-base md:mb-6">
              Contatos do cliente
            </legend>

            <div className="space-y-4">
              <div className="space-y-6">
                {contactFields.map(({ id }, index) => {
                  return (
                    <OpenInputEmail
                      key={id}
                      canRemove={canRemoveContactsField}
                      handleRemove={() => handleRemoveContactField(index)}
                      header={
                        <Controller
                          name={`contacts.${index}.id`}
                          control={control}
                          render={({
                            field: { value = '', onChange },
                            fieldState: { error },
                          }) => {
                            const filteredContactOptions = getFilteredOptions(
                              'contacts',
                              index,
                              contactsOptions
                            )

                            return (
                              <FormGroup error={error?.message}>
                                <InfiniteSelect
                                  options={filteredContactOptions}
                                  hasNextPage={hasNextContactPages}
                                  defaultValue={String(value)}
                                  onValueChange={(value) => {
                                    const { triggerValue, label: name } =
                                      filteredContactOptions.find(
                                        (option) => option.value === value
                                      ) as SelectOption

                                    onChange(value)
                                    setValue(`contacts.${index}.name`, name)
                                    setValue(
                                      `contacts.${index}.email`,
                                      triggerValue || ''
                                    )
                                  }}
                                  onSearch={setSearchedContact}
                                  onLoadMoreOptions={fetchNextContactPages}
                                  placeholder="Selecione um contato"
                                />
                              </FormGroup>
                            )
                          }}
                        />
                      }
                    >
                      <Controller
                        name={`contacts.${index}.email`}
                        control={control}
                        render={({
                          field: { value = '', onChange },
                          fieldState: { invalid, isDirty, error },
                        }) => (
                          <Input
                            isValid={!invalid && isDirty}
                            value={value}
                            onChange={onChange}
                            errorMessage={error?.message}
                            placeholder="email@email.com.br"
                          />
                        )}
                      />
                    </OpenInputEmail>
                  )
                })}
              </div>

              <Button
                type="button"
                size="small"
                variant="outline"
                onClick={handleAddNewContactFields}
                className="whitespace-nowrap"
              >
                <AddSvg />
                Adicionar contato
              </Button>
            </div>
          </div>

          <div>
            <legend className="mb-4 text-sm font-bold md:text-base md:mb-6">
              Researchers
            </legend>

            <div className="space-y-4">
              <div className="space-y-6">
                {administrativeUsersFields.map(({ id }, index) => {
                  return (
                    <OpenInputEmail
                      key={id}
                      canRemove={canRemoveAdministrativeUser}
                      handleRemove={() => handleRemoveResearcherFields(index)}
                      header={
                        <Controller
                          name={`administrativeUsers.${index}.email`}
                          control={control}
                          render={({
                            field: { value = '', onChange },
                            fieldState: { error },
                          }) => {
                            const filteredAdministrativeUsersOptions =
                              getFilteredOptions(
                                'administrativeUsers',
                                index,
                                administrativeUsersOptions,
                                'email'
                              )

                            return (
                              <FormGroup error={error?.message}>
                                <InfiniteSelect
                                  options={filteredAdministrativeUsersOptions}
                                  hasNextPage={hasNextAdministrativeUsers}
                                  defaultValue={String(value)}
                                  onValueChange={(value) => {
                                    const { label: name } =
                                      filteredAdministrativeUsersOptions.find(
                                        (option) => option.value === value
                                      ) as SelectOption

                                    onChange(value)
                                    setValue(
                                      `administrativeUsers.${index}.name`,
                                      name
                                    )
                                  }}
                                  onSearch={setSearchAdministrativeUser}
                                  onLoadMoreOptions={
                                    fetchNextAdministrativeUsersPage
                                  }
                                  placeholder="Selecione um Reseacher"
                                />
                              </FormGroup>
                            )
                          }}
                        />
                      }
                    >
                      <Controller
                        name={`administrativeUsers.${index}.email`}
                        control={control}
                        render={({
                          field: { value = '', onChange },
                          fieldState: { invalid, isDirty, error },
                        }) => (
                          <Input
                            isValid={!invalid && isDirty}
                            value={value}
                            onChange={onChange}
                            errorMessage={error?.message}
                            placeholder="email@email.com.br"
                          />
                        )}
                      />
                    </OpenInputEmail>
                  )
                })}
              </div>

              <Button
                type="button"
                size="small"
                variant="outline"
                onClick={handleAddNewResearcherFields}
                className="whitespace-nowrap"
              >
                <AddSvg />
                Adicionar researcher
              </Button>
            </div>
          </div>

          <div>
            <legend className="mb-4 text-sm font-bold md:text-base md:mb-6">
              Experts
            </legend>

            <div className="space-y-4">
              <div className="space-y-6">
                {expertFields.map(({ id }, index) => {
                  return (
                    <OpenInputEmail
                      key={id}
                      canRemove={canRemoveExpertField}
                      handleRemove={() => handleRemoveExpertFields(index)}
                      header={
                        <Controller
                          name={`experts.${index}.id`}
                          control={control}
                          render={({
                            field: { value = '', onChange },
                            fieldState: { error },
                          }) => {
                            const filteredExpertsOptions = getFilteredOptions(
                              'experts',
                              index,
                              expertsOptions
                            )

                            return (
                              <FormGroup error={error?.message}>
                                <InfiniteSelect
                                  options={filteredExpertsOptions}
                                  hasNextPage={hasNextExpertPages}
                                  defaultValue={String(value)}
                                  onValueChange={(value) => {
                                    const { triggerValue, label: name } =
                                      filteredExpertsOptions.find(
                                        (option) => option.value === value
                                      ) as SelectOption

                                    onChange(value)
                                    setValue(`experts.${index}.name`, name)
                                    setValue(
                                      `experts.${index}.email`,
                                      triggerValue || ''
                                    )
                                  }}
                                  onSearch={setSearchedExpert}
                                  onLoadMoreOptions={fetchNextExpertPages}
                                  placeholder="Selecione um expert"
                                />
                              </FormGroup>
                            )
                          }}
                        />
                      }
                    >
                      <Controller
                        name={`experts.${index}.email`}
                        control={control}
                        render={({
                          field: { value = '', onChange },
                          fieldState: { invalid, isDirty, error },
                        }) => (
                          <Input
                            isValid={!invalid && isDirty}
                            value={value}
                            onChange={onChange}
                            errorMessage={error?.message}
                            placeholder="email@email.com.br"
                          />
                        )}
                      />
                    </OpenInputEmail>
                  )
                })}
              </div>

              <Button
                type="button"
                size="small"
                variant="outline"
                onClick={handleAddNewExpertFields}
                className="whitespace-nowrap"
              >
                <AddSvg />
                Adicionar expert
              </Button>
            </div>
          </div>

          <div>
            <legend className="mb-4 text-sm font-bold md:text-base md:mb-6">
              Analistas
            </legend>

            <div className="space-y-4">
              <div className="space-y-6">
                {analystFields.map(({ id }, index) => {
                  return (
                    <div className="flex w-full gap-2" key={id}>
                      <div className="flex flex-col w-full gap-4">
                        <Controller
                          name={`analysts.${index}.id`}
                          control={control}
                          render={({
                            field: { value = '', onChange },
                            fieldState: { error },
                          }) => {
                            const filteredAnalystOptions = getFilteredOptions(
                              'analysts',
                              index,
                              analystOptions,
                            )

                            return (
                              <FormGroup error={error?.message}>
                                <InfiniteSelect
                                  options={filteredAnalystOptions}
                                  hasNextPage={hasNextAnalystsPages}
                                  defaultValue={String(value)}
                                  onValueChange={(value) => {
                                    const { label: name } =
                                      filteredAnalystOptions.find(
                                        (option) => option.value === value
                                      ) as SelectOption

                                    onChange(value)
                                    setValue(`analysts.${index}.name`, name)
                                  }}
                                  onSearch={setSearchedAnalyst}
                                  onLoadMoreOptions={fetchNextAnalystsPages}
                                  placeholder="Selecione um analista"
                                />
                              </FormGroup>
                            )
                          }}
                        />

                        <Controller
                          name={`analysts.${index}.email`}
                          control={control}
                          render={({
                            field: { value = '', onChange },
                            fieldState: { invalid, isDirty, error },
                          }) => (
                            <Input
                              isValid={!invalid && isDirty}
                              value={value}
                              onChange={onChange}
                              errorMessage={error?.message}
                              placeholder="email@email.com.br"
                            />
                          )}
                        />
                      </div>

                      <div className="mt-4">
                        {canRemoveAnalystField && (
                          <Trash
                            className="cursor-pointer h-7 w-7 text-grey-700"
                            onClick={() => handleRemoveAnalystFields(index)}
                          />
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>

              <Button
                type="button"
                size="small"
                variant="outline"
                onClick={handleAddNewAnalystFields}
                className="whitespace-nowrap"
              >
                <AddSvg />
                Adicionar analista
              </Button>
            </div>
          </div>
        </div>

        <fieldset>
          <legend className="mb-4 text-sm font-bold md:text-base md:mb-6">
            Informações do invite
          </legend>

          <div>
            <div className="mb-4">
              <Controller
                name="summary"
                control={control}
                render={({
                  field: { value = '', onChange },
                  fieldState: { invalid, isDirty, error },
                }) => (
                  <Input
                    label="Título"
                    isValid={!invalid && isDirty}
                    value={value}
                    onChange={onChange}
                    errorMessage={error?.message}
                  />
                )}
              />
            </div>

            <div>
              <Controller
                name="description"
                control={control}
                render={({
                  field: { value = '', onChange },
                  fieldState: { error },
                }) => (
                  <FormGroup label="Descrição" error={error?.message}>
                    <textarea
                      placeholder="Digite aqui sua justificativa"
                      className={clsx(
                        'p-3 pr-10 h-[108px] resize-none lg:pr-12 w-full rounded-lg outline-none border-grey-500 border-1 text-xs text-grey-800 placeholder:text-grey-500  disabled:bg-grey-300 disabled:text-grey-500 lg:py-4 lg:text-sm',
                        {
                          'border-red-500': error?.message,
                          'focus:border-grey-700': !error?.message,
                        }
                      )}
                      value={value}
                      onChange={onChange}
                    />
                  </FormGroup>
                )}
              />
            </div>
          </div>
        </fieldset>
      </section>

      <Button
        type="button"
        disabled={!formState.isValid}
        onClick={onGoToNextStep}
        className="mx-auto mt-10 whitespace-nowrap"
      >
        <CalendarSvg
          className={clsx('stroke-white', { 'opacity-40': !formState.isValid })}
        />
        Selecionar data
      </Button>
    </form>
  )
}
